import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import { find, join, lift, map, propEq, toPairs, filter, not } from "ramda";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import TablePagination from "@material-ui/core/TablePagination";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import { isBlank, mapWithIndex, notBlank } from "../util/ext";
import Tooltip from "@material-ui/core/Tooltip";
import { Box, TableFooter } from "@material-ui/core";
import Collapse from "@material-ui/core/Collapse";
import Typography from "@material-ui/core/Typography";
import {
  useContentProviderDelete,
  useContentProviderEndpointDelete,
  useContentProviderEndpoints,
} from "./contentProviderServices";

import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderPlus, faEdit } from "@fortawesome/pro-regular-svg-icons";
import YesNoCard from "../components/YesNoCard";
import ConfirmButton from "../components/ConfirmButton";
import { useOrganizations } from "../user-admin/userServices";
import { Skeleton } from "@material-ui/lab";

const ROWS_PER_PAGE = 15;
const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  container: {
    maxHeight: "calc(100vh - 350px)",
    overflow: "scroll",
  },

  btnRoot: {
    lineHeight: 1.43,
  },

  btnLabel: {
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cell: {
    minWidth: 80,
  },

  cardCell: {
    minWidth: 300,
  },

  typeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const useRowStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      borderBottom: "unset",
    },
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },
}));

const useSubTableStyles = makeStyles(theme => ({
  subHeader: {
    fontSize: "1.4rem",
  },

  cell: {
    fontSize: "1.4rem",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
  },
}));

const SubTable = props => {
  const { provider, findModuleName, onEditEndpointClick, onError } = props;
  const classes = useSubTableStyles();

  const [endpoints, setEndpoints] = useState([]);
  const endpointsResponse = useContentProviderEndpoints(provider.id);
  useEffect(() => {
    if (endpointsResponse.isSuccess) {
      const activeEndpoints = filter(ep => ep.active, endpointsResponse.data);
      setEndpoints(activeEndpoints);
    }
  }, [endpointsResponse.isSuccess, endpointsResponse.data]);

  const providerEndpointDelete = useContentProviderEndpointDelete();
  const onDeleteEndpointClick = useCallback(
    async endpointId => {
      try {
        await providerEndpointDelete.mutateAsync({
          resourceIds: {
            contentProviderId: provider.id,
            contentProviderEndpointId: endpointId,
          },
        });
      } catch (err) {
        onError("error", [`Unable to delete Endpoint : ${err.message}`]);
      }
    },
    [onError, providerEndpointDelete, provider.id],
  );

  return (
    <Fragment>
      {endpointsResponse.isLoading && <PanelLoadingIndicator />}

      {endpointsResponse.isSuccess && notBlank(endpoints) ? (
        <Box margin={3}>
          <Typography variant="h4" gutterBottom component="div">
            Endpoints
          </Typography>

          <Table stickyHeader aria-label="endpoints">
            <TableHead>
              <TableRow>
                <TableCell className={classes.subHeader}>Name</TableCell>
                <TableCell className={classes.subHeader}>Location</TableCell>
                <TableCell
                  className={classes.subHeader}
                  style={{ maxWidth: 75 }}
                >
                  Text Direction
                </TableCell>
                <TableCell className={classes.subHeader}>Parser</TableCell>
                <TableCell
                  className={classes.subHeader}
                  style={{ maxWidth: 75 }}
                >
                  Auto Approve
                </TableCell>
                <TableCell
                  className={classes.subHeader}
                  style={{ maxWidth: 75 }}
                >
                  Disable Archive
                </TableCell>
                <TableCell className={classes.subHeader}>
                  Default Modules
                </TableCell>
                <TableCell className={classes.subHeader}>Overrides</TableCell>
                <TableCell className={classes.subHeader}>Defaults</TableCell>
                <TableCell
                  key="endpoint-actions"
                  align="center"
                  style={{ minWidth: 75 }}
                ></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {endpoints.map(endpoint => (
                <TableRow hover key={endpoint.id}>
                  <TableCell
                    key={"name" + endpoint.id}
                    component="th"
                    scope="row"
                  >
                    <Box>
                      <Box fontSize="1.4rem">{endpoint.name}</Box>
                      <Box fontSize="1.2rem">({endpoint.id})</Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    key={"location" + endpoint.id}
                    className={classes.cell}
                  >
                    {endpoint.location}
                  </TableCell>

                  <TableCell
                    key={"textDirection" + endpoint.id}
                    className={classes.cell}
                  >
                    {endpoint.textDirectionality}
                  </TableCell>

                  <TableCell
                    key={"metadataParser" + endpoint.id}
                    className={classes.cell}
                  >
                    {endpoint.metadataParser}
                  </TableCell>

                  <TableCell
                    key={"autoApprove" + endpoint.id}
                    align="center"
                    className={classes.cell}
                  >
                    <YesNoCard
                      value={
                        notBlank(endpoint.autoApprove)
                          ? endpoint.autoApprove
                          : false
                      }
                      showNoIcon={false}
                    />
                  </TableCell>

                  <TableCell
                    key={"disableArchive" + endpoint.id}
                    align="center"
                    className={classes.cell}
                  >
                    <YesNoCard
                      value={
                        notBlank(endpoint.disableArchive)
                          ? endpoint.disableArchive
                          : false
                      }
                      showNoIcon={false}
                    />
                  </TableCell>

                  <TableCell
                    key={"defaultModules" + endpoint.id}
                    className={classes.cell}
                  >
                    {notBlank(endpoint.defaultModules)
                      ? mapWithIndex(
                          (mod, index) => (
                            <Box key={`module-${endpoint.id}-${index}`}>
                              {mod}
                            </Box>
                          ),
                          map(findModuleName, endpoint.defaultModules),
                        )
                      : ""}
                  </TableCell>
                  <TableCell
                    key={"overrides" + endpoint.id}
                    className={classes.cell}
                  >
                    {notBlank(endpoint.overrides)
                      ? mapWithIndex(
                          (override, index) => (
                            <Box key={`override-${endpoint.id}-${index}`}>
                              {override}
                            </Box>
                          ),
                          lift(join(" -> "))(toPairs(endpoint.overrides)),
                        )
                      : ""}
                  </TableCell>

                  <TableCell
                    key={"defaults" + endpoint.id}
                    className={classes.cell}
                  >
                    {notBlank(endpoint.defaults)
                      ? mapWithIndex(
                          (def, index) => (
                            <Box key={`default-${endpoint.id}-${index}`}>
                              {def}
                            </Box>
                          ),
                          lift(join(" -> "))(toPairs(endpoint.defaults)),
                        )
                      : ""}
                  </TableCell>

                  <TableCell align="center" style={{ width: 100 }}>
                    <Box>
                      <Tooltip title="Edit Endpoint">
                        <IconButton
                          classes={{ label: classes.icon }}
                          onClick={() =>
                            onEditEndpointClick(provider, endpoint.id)
                          }
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </IconButton>
                      </Tooltip>

                      <ConfirmButton
                        handleDelete={() => onDeleteEndpointClick(endpoint.id)}
                        dialogTitle="Remove Endpoint"
                        dialogBody="Are you sure you want to remove this endpoint?"
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box padding={5} fontSize="1.6rem" fontStyle="italic">
          No Endpoints exist for this Content Provider
        </Box>
      )}
    </Fragment>
  );
};

const Row = props => {
  const {
    row,
    findModuleName,
    onAddEndpointClick,
    onEditEndpointClick,
    onEditProviderClick,
    onError,
  } = props;
  const [open, setOpen] = React.useState(false);
  const classes = useRowStyles();

  const [libraries, setLibraries] = useState([]);
  const libraryResponse = useOrganizations();
  useEffect(() => {
    if (libraryResponse.isSuccess) {
      setLibraries(libraryResponse.data);
    }
  }, [libraryResponse.isSuccess, libraryResponse.data]);

  const findOrganization = organizationId => {
    const organization = find(
      propEq("organizationId", organizationId),
      libraries,
    );

    return (
      <Typography color={not(organization.active) ? "error" : "inherit"}>
        {organization.organizationName}
      </Typography>
    );
  };

  const providerDelete = useContentProviderDelete();
  const onDeleteProviderClick = useCallback(
    async providerId => {
      try {
        await providerDelete.mutateAsync({
          resourceIds: {
            contentProviderId: providerId,
          },
        });
      } catch (err) {
        onError("error", [
          `Unable to delete Content Provider : ${err.message}`,
        ]);
      }
    },
    [onError, providerDelete],
  );

  return (
    <React.Fragment>
      <TableRow hover className={classes.root} selected={open} key={row.id}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell scope="row">{row.name}</TableCell>

        <TableCell>{row.id}</TableCell>

        <TableCell>
          {isBlank(libraries) ? (
            <Skeleton variant="text" />
          ) : (
            notBlank(row.organizationId) && findOrganization(row.organizationId)
          )}
        </TableCell>

        <TableCell align="center" style={{ width: 100 }}>
          <Box>
            <Tooltip title="Edit Provider">
              <IconButton
                classes={{ label: classes.icon }}
                onClick={() => onEditProviderClick(row)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Add Endpoint">
              <IconButton
                classes={{ label: classes.icon }}
                onClick={() => onAddEndpointClick(row)}
              >
                <FontAwesomeIcon icon={faFolderPlus} />
              </IconButton>
            </Tooltip>

            <ConfirmButton
              handleDelete={() => onDeleteProviderClick(row.id)}
              dialogTitle="Remove Provider"
              dialogBody="Are you sure you want to remove this provider?"
            />
          </Box>
        </TableCell>
      </TableRow>

      <TableRow selected={open}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {open && (
              <SubTable
                provider={row}
                findModuleName={findModuleName}
                onEditEndpointClick={onEditEndpointClick}
                onError={onError}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const ContentProvidersTable = props => {
  const {
    contentProviderList,
    modules,
    page,
    onPageChange,
    onScrollVisible,
    onAddEndpointClick,
    onEditEndpointClick,
    onEditProviderClick,
    onError,
  } = props;

  const classes = useStyles();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const findModuleName = useCallback(
    id => {
      const module = find(propEq("id", id), modules);
      return notBlank(module) ? module.name : "...";
    },
    [modules],
  );

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer className={classes.container} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Content Providers">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell key="contentProviderName" align="left">
                Name
              </TableCell>

              <TableCell key="contentProviderId" align="left">
                Content Provider Id
              </TableCell>

              <TableCell key="organizationId" align="left">
                Organization
              </TableCell>

              <TableCell
                key="actions"
                align="center"
                style={{ minWidth: 175 }}
              ></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {contentProviderList
              .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
              .map(cp => (
                <Row
                  key={cp.id}
                  row={cp}
                  findModuleName={findModuleName}
                  onAddEndpointClick={onAddEndpointClick}
                  onEditEndpointClick={onEditEndpointClick}
                  onEditProviderClick={onEditProviderClick}
                  onError={onError}
                />
              ))}
          </TableBody>
          <TableFooter>
            <TablePagination
              classes={{
                root: classes.pagination,
              }}
              colSpan={4}
              count={contentProviderList.length}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              rowsPerPage={ROWS_PER_PAGE}
              page={page}
              onChangePage={onPageChange}
            />
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ContentProvidersTable;
